<template>
    <div>
      <v-card class="mb-4 rounded elevation-2 border" color="white" outlined>
        <!-- Header / Title Section -->
        <v-card-title class="d-flex flex-wrap align-center justify-space-between py-3 px-4 grey lighten-4">
          <div class="d-flex flex-column mb-1 mb-sm-0">
            <div class="text-h6 font-weight-bold" :style="{ color: getThemeColor }">
              <template v-if="selectedFile">
                {{ clientOrPatientName }}
              </template>
              <template v-else>
                {{ getUserCompany?.industry || 'Practice' }} Quotes, Billing & Invoicing System
              </template>
            </div>
  
            <div v-if="selectedFile" class="text-caption grey--text text--darken-1 font-weight-medium">
              File No: {{ selectedFile.reference_number || 'N/A' }}
              <strong v-if="selectedFile.invoice_number"> | Invoice No: {{ selectedFile.invoice_number }}</strong>
            </div>
          </div>
  
          <!-- Badges & Status Indicators -->
          <div v-if="selectedFile" class="d-flex align-center flex-wrap gap-2">
            <!-- EMIS Tag (Common to both if applicable) -->
            <!-- <v-chip v-if="selectedFile.emis" small color="indigo" class="white--text font-weight-medium mr-2">
              <v-icon left small>mdi-school-outline</v-icon>
              EMIS: {{ selectedFile.emis }}
            </v-chip> -->
  
            <!-- Social Work Specific: Confidentiality Level -->
            <div class="d-flex align-center flex-wrap gap-2 mt-2 mt-sm-0">
              <v-chip
                color="primary"
                variant="tonal"
                size="small"
                class="font-weight-medium"
                prepend-icon="mdi-text-short"
              >
                <span class="text-truncate" style="max-width: 300px;">
                  <ProFormaGenerator :invoice="selectedFile" 
                  :title="selectedFile.invoice_number ? 'Invoice' : 'Quotation'" 
                  :buttunWithText="true"></ProFormaGenerator>
                </span>
              </v-chip>
            </div>

            <!-- <v-chip
              v-if="isSocialWork && selectedFile.confidentiality_level"
              small
              :color="getConfidentialityColor(selectedFile.confidentiality_level)"
              class="white--text font-weight-bold text-uppercase"
            >
              <v-icon left small>mdi-shield-lock-outline</v-icon>
              {{ selectedFile.confidentiality_level }}
            </v-chip>
  
            < Private Practice Specific: Medical Aid Badge>
            <v-chip
              v-if="isPrivatePractice && selectedFile.medical_aid_name"
              small
              color="teal"
              class="white--text font-weight-medium"
            >
              <v-icon left small>mdi-card-account-details-outline</v-icon>
              {{ selectedFile.medical_aid_name }}
            </v-chip> -->
          </div>
        </v-card-title>
  
        <v-divider></v-divider>
  
        <!-- Details Grid -->
        <v-card-text v-if="selectedFile" class="py-3 px-4">
          <!-- Common Description Row -->
          <v-row dense class="mb-2">
            <v-col cols="12">
              <div class="text-caption grey--text text--darken-1">File Title / Description</div>
              <div class="text-body-2 font-weight-bold primary--text">
                <v-icon small color="primary" class="mr-1">mdi-folder-text-outline</v-icon>
                {{ selectedFile.description || 'No description provided' }}
              </div>
            </v-col>
          </v-row>
  
          <v-divider class="my-2"></v-divider>
  
          <!-- Dynamic Industry Row -->
          <v-row dense>
            <!-- COMMON FIELD: Client / Patient ID Ref -->
            <v-col cols="12" sm="6" md="3">
              <div class="text-caption grey--text text--darken-1">{{ isPrivatePractice ? 'Patient Ref' : 'Client Ref' }}</div>
              <div class="text-body-2 font-weight-medium">
                <v-icon small class="mr-1 grey--text">mdi-account-badge-outline</v-icon>
                {{ selectedFile.client_reference || 'N/A' }}
              </div>
            </v-col>
  
            <!-- PRIVATE PRACTICE SPECIFIC FIELDS -->
            <template v-if="isPrivatePractice">
              <v-col cols="12" sm="6" md="3">
                <div class="text-caption grey--text text--darken-1">ICD-10 Code</div>
                <div class="text-body-2 font-weight-medium">
                  <v-icon small class="mr-1 grey--text">mdi-medical-bag</v-icon>
                  {{ selectedFile.icd10_code || 'None' }}
                </div>
              </v-col>
  
              <v-col cols="12" sm="6" md="3">
                <div class="text-caption grey--text text--darken-1">Medical Aid Number</div>
                <div class="text-body-2 font-weight-medium text-truncate">
                  <v-icon small class="mr-1 grey--text">mdi-numeric</v-icon>
                  {{ selectedFile.medical_aid_number || 'Private / Cash' }}
                </div>
              </v-col>
  
              <v-col cols="12" sm="6" md="3">
                <div class="text-caption grey--text text--darken-1">Practitioner Ref</div>
                <div class="text-body-2 font-weight-medium">
                  <v-icon small class="mr-1 grey--text">mdi-doctor</v-icon>
                  {{ selectedFile.practitioner_reference || 'Unassigned' }}
                </div>
              </v-col>
            </template>
  
            <!-- SOCIAL WORK SPECIFIC FIELDS -->
            <template v-if="isSocialWork">
              <v-col cols="12" sm="6" md="3">
                <div class="text-caption grey--text text--darken-1">Case Type</div>
                <div class="text-body-2 font-weight-medium">
                  <v-icon small class="mr-1 grey--text">mdi-clipboard-text-outline</v-icon>
                  {{ selectedFile.case_type || 'General Intervention' }}
                </div>
              </v-col>
  
              <v-col cols="12" sm="6" md="3">
                <div class="text-caption grey--text text--darken-1">Referral Source</div>
                <div class="text-body-2 font-weight-medium text-truncate">
                  <v-icon small class="mr-1 grey--text">mdi-account-arrow-right-outline</v-icon>
                  {{ selectedFile.referral_source || 'Self / Walk-in' }}
                </div>
              </v-col>
  
              <v-col cols="12" sm="6" md="3">
                <div class="text-caption grey--text text--darken-1">Caseworker Ref</div>
                <div class="text-body-2 font-weight-medium">
                  <v-icon small class="mr-1 grey--text">mdi-account-tie-outline</v-icon>
                  {{ selectedFile.practitioner_reference || 'Unassigned' }}
                </div>
              </v-col>
            </template>
          </v-row>
  
          <!-- Optional Clinical Notes Preview -->
          <v-row v-if="selectedFile.notes" dense class="mt-2">
            <v-col cols="12">
              <div class="text-caption grey--text text--darken-1">Notes Summary</div>
              <div class="text-caption font-italic grey--text text--darken-3 text-truncate">
                "{{ selectedFile.notes }}"
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </div>
  </template>
  
  <script>
  import ProFormaGenerator from '@/modules/CaseManager/ProFormaGenerator.vue'

  export default {
    name: "PracticeHeader",
    props: {
      selectedFile: {
        type: Object,
        default: null,
      },
    },
    components:{
      ProFormaGenerator
    },
    computed: {
      getThemeColor() {
        return this.$store?.getters?.getThemeColor || '#1976D2';
      },
      getUserCompany() {
        return this.$store?.getters?.getUserCompany || {};
      },
      // Determines whether app is in 'privatePractice' or 'social' mode
      getAppMode() {
        return this.$store?.getters?.getAppMode || 'privatePractice';
      },
      isPrivatePractice() {
        return this.getAppMode === 'privatePractice';
      },
      isSocialWork() {
        return this.getAppMode === 'social';
      },
      // Resolves client or patient name gracefully
      clientOrPatientName() {
        if (!this.selectedFile) return '';
        return (
          this.selectedFile.client?.name ||
          this.selectedFile.patient?.name ||
          (this.isPrivatePractice ? 'Patient File' : 'Client File')
        );
      },
    },
    methods: {
      getConfidentialityColor(level) {
        switch (String(level).toLowerCase()) {
          case 'high_security':
          case 'restricted':
            return 'red darken-2';
          case 'sensitive':
            return 'orange darken-2';
          case 'standard':
          default:
            return 'blue-grey';
        }
      },
    },
  };
  </script>